// V4-ZERNIO-COST-PURGE 2026-06-05 — niveau d'alerte agrégé des canaux pour le badge
// du menu "Canaux" dans la sidebar gauche.
//
//  - 'danger' (rouge)  : au moins un canal a une déconnexion PERSISTÉE — purge auto
//                        (`purged`), token expiré (`expired`), déconnexion volontaire
//                        (`disconnected`), ou token déjà passé.
//  - 'warning' (jaune) : au moins un canal entre dans la fenêtre des 7 derniers jours
//                        avant déconnexion (purge programmée ou expiration OAuth proche).
//  - null              : tout va bien.
//
// V4-CHANNEL-WARNING-HARDEN 2026-06-15 — le statut `error` n'est PLUS un déclencheur
// de danger : il est posé par les sondes de santé Zernio (`check_account_health`) qui
// retombent sur `error` au moindre hiccup transitoire de l'API, et ce statut peut être
// persisté en ES sur un canal pourtant connecté → faux « canal déconnecté » dans la
// sidebar (cas prod sebastien@). Seules les déconnexions persistées réelles alertent ;
// l'utilisateur peut re-sonder via le bouton « rafraîchir la connexion » du canal.
import type { CommunicationChannel } from '../types/channel';

export type ChannelAlertLevel = 'danger' | 'warning' | null;

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function isPast(iso?: string, now = Date.now()): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t <= now;
}

function isWithin(iso: string | undefined, windowMs: number, now = Date.now()): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t > now && t - now <= windowMs;
}

export function computeChannelAlertLevel(
  channels: CommunicationChannel[] | undefined | null,
  now = Date.now(),
): ChannelAlertLevel {
  if (!channels || channels.length === 0) return null;

  let hasWarning = false;
  for (const ch of channels) {
    const status = ch.zernioStatus;
    // Déconnexion PERSISTÉE → rouge (prioritaire, on peut court-circuiter).
    // `error` exclu volontairement (transitoire, cf en-tête V4-CHANNEL-WARNING-HARDEN).
    if (status === 'purged' || status === 'expired' || status === 'disconnected') return 'danger';
    if (isPast(ch.tokenExpiresAt, now)) return 'danger';
    // Fenêtre des 7 jours avant déconnexion → jaune.
    if (status === 'pending_purge') hasWarning = true;
    else if (isWithin(ch.purgeScheduledAt, SEVEN_DAYS_MS, now)) hasWarning = true;
    else if (isWithin(ch.tokenExpiresAt, SEVEN_DAYS_MS, now)) hasWarning = true;
  }
  return hasWarning ? 'warning' : null;
}
